import { SAMPLE_DOCUMENT_BLOB_ID } from '../types';
import type {
  AuditBatch,
  AuditBatchCaseSummary,
  AuditBatchState,
  AuditCase,
  AuditCaseInternal,
  CaseRecord,
  CaseStatus,
  CodedFields,
} from '../types';
import { AUDIT_FIELDS, discrepancyCount } from './fields';

// Audit cases + batch builders (HUB-504), ported from the prototype's AUDIT_CASES.
// A = the autonomous AI agent's coding, B = the human coder's coding. This module
// is the ONLY place the agent↔coder mapping and the per-case display flip live;
// everything downstream (the queue + case views) consumes the BLIND shapes below,
// which withhold provenance. No true PHI — deterministic sample data.

/** Raw coded values before qualifier flags are merged in. */
type RawCoded = Omit<CodedFields, 'emergency' | 'unusual_position'>;

interface RawCase {
  id: string;
  batchId: string;
  patientFull: string;
  patient: string;
  mrn: string;
  dob: string;
  sex: string;
  ageYrs: number;
  dos: string;
  facility: string;
  surgeon: string;
  anesth: string;
  payer: string;
  service: string;
  priority: string;
  status: CaseStatus;
  receivedAgo: string;
  /** A = autonomous agent */
  A: RawCoded;
  /** B = human coder */
  B: RawCoded;
  record: CaseRecord;
}

const RAW_CASES: RawCase[] = [
  // ===== BATCH b-hm-0612 · Houston Methodist · Jun 12, 2026 (5 cases) =====
  {
    id: 'AC-100482', batchId: 'b-hm-0612', patientFull: 'Robert Thompson', patient: 'Thompson, R.', mrn: 'MRN-4471902', dob: '03/14/1958', sex: 'M', ageYrs: 68,
    dos: 'Jun 12, 2026', facility: 'Houston Methodist', surgeon: 'A. Nwosu, MD', anesth: 'L. Park, MD · CRNA J. Bello',
    payer: 'Medicare', service: 'Inpatient', priority: 'high', status: 'unreviewed', receivedAgo: '2h',
    A: { surgical_cpt: '47563', anesthesia_cpt: '00790', primary_icd: 'K80.10', additional_icd: ['E11.9', 'I10'], modifiers: ['AA'], phys_status: 'P3' },
    B: { surgical_cpt: '47562', anesthesia_cpt: '00790', primary_icd: 'K80.20', additional_icd: ['E11.9'], modifiers: ['QK'], phys_status: 'P2' },
    record: { indication: 'Symptomatic cholelithiasis with chronic cholecystitis', procedure: 'Laparoscopic cholecystectomy with intraoperative cholangiogram', findings: 'Distended, chronically inflamed gallbladder with multiple calculi and wall thickening. Intraoperative cholangiogram performed via cystic duct — normal ductal anatomy, no filling defects.', comorbidities: ['Type 2 diabetes mellitus (A1c 8.9%)', 'Essential hypertension'], airway: 'Mallampati II; uneventful direct laryngoscopy, grade I view', asaObserved: 'ASA III — poorly controlled diabetes and hypertension', technique: 'General endotracheal anesthesia; anesthesiologist personally performing case', agents: 'Propofol, sevoflurane, rocuronium, fentanyl', anesStart: '07:42', anesStop: '09:18', surgStart: '08:05', surgStop: '09:02' },
  },
  {
    id: 'AC-100485', batchId: 'b-hm-0612', patientFull: 'Marcus Reed', patient: 'Reed, M.', mrn: 'MRN-4472118', dob: '05/22/1971', sex: 'M', ageYrs: 55,
    dos: 'Jun 12, 2026', facility: 'Houston Methodist', surgeon: 'K. Duval, MD', anesth: 'L. Park, MD',
    payer: 'BCBS of Texas', service: 'Outpatient', priority: 'med', status: 'unreviewed', receivedAgo: '2h',
    A: { surgical_cpt: '29827', anesthesia_cpt: '01630', primary_icd: 'M75.101', additional_icd: [], modifiers: ['AA', 'QS'], phys_status: 'P2' },
    B: { surgical_cpt: '29827', anesthesia_cpt: '01622', primary_icd: 'M75.101', additional_icd: [], modifiers: ['AA'], phys_status: 'P2' },
    record: { indication: 'Full-thickness tear of the right rotator cuff, failed conservative therapy', procedure: 'Right shoulder arthroscopy with rotator cuff repair', findings: 'Complete supraspinatus tear; double-row repair performed. A single-shot interscalene brachial plexus block was placed under ultrasound guidance for post-operative analgesia.', comorbidities: ['Controlled hypertension'], airway: 'General LMA', asaObserved: 'ASA II', technique: 'General anesthesia with pre-operative interscalene block for post-op pain', agents: 'Propofol, sevoflurane; ropivacaine (block)', anesStart: '09:10', anesStop: '10:48', surgStart: '09:35', surgStop: '10:32' },
  },
  {
    id: 'AC-100488', batchId: 'b-hm-0612', patientFull: 'Sophia Nguyen', patient: 'Nguyen, S.', mrn: 'MRN-9902213', dob: '12/05/1988', sex: 'F', ageYrs: 37,
    dos: 'Jun 12, 2026', facility: 'Houston Methodist', surgeon: 'A. Nwosu, MD', anesth: 'S. Ibrahim, MD',
    payer: 'UnitedHealthcare', service: 'Outpatient', priority: 'med', status: 'unreviewed', receivedAgo: '3h',
    A: { surgical_cpt: '27447', anesthesia_cpt: '01402', primary_icd: 'M17.12', additional_icd: [], modifiers: ['AA'], phys_status: 'P1' },
    B: { surgical_cpt: '29881', anesthesia_cpt: '01382', primary_icd: 'M17.12', additional_icd: [], modifiers: ['AA', 'LT'], phys_status: 'P1' },
    record: { indication: 'Left knee medial meniscus tear, failed conservative management', procedure: 'Left knee arthroscopy with partial medial meniscectomy', findings: 'Complex tear of the medial meniscus; partial meniscectomy performed. Articular surfaces intact. No arthroplasty performed.', comorbidities: ['None'], airway: 'LMA general', asaObserved: 'ASA I', technique: 'General anesthesia, anesthesiologist personally performed', agents: 'Propofol, sevoflurane', anesStart: '09:30', anesStop: '10:18', surgStart: '09:48', surgStop: '10:10' },
  },
  {
    id: 'AC-100491', batchId: 'b-hm-0612', patientFull: 'Harold Kim', patient: 'Kim, H.', mrn: 'MRN-2204471', dob: '09/30/1949', sex: 'M', ageYrs: 76,
    dos: 'Jun 12, 2026', facility: 'Houston Methodist', surgeon: 'I. Garrison, MD', anesth: 'D. Chen, MD',
    payer: 'Medicare', service: 'Outpatient', priority: 'low', status: 'unreviewed', receivedAgo: '4h',
    A: { surgical_cpt: '66984', anesthesia_cpt: '00142', primary_icd: 'H25.11', additional_icd: [], modifiers: ['RT', 'QS'], phys_status: 'P2' },
    B: { surgical_cpt: '66984', anesthesia_cpt: '00142', primary_icd: 'H25.12', additional_icd: [], modifiers: ['LT', 'QS'], phys_status: 'P2' },
    record: { indication: 'Visually significant age-related nuclear cataract, right eye', procedure: 'Phacoemulsification with posterior chamber IOL, right eye (OD)', findings: 'Dense nuclear sclerotic cataract OD; uncomplicated phaco with in-the-bag IOL.', comorbidities: ['Controlled hypertension'], airway: 'MAC — topical/peribulbar block', asaObserved: 'ASA II', technique: 'Monitored anesthesia care with peribulbar block, right eye', agents: 'Propofol, midazolam; local by surgeon', anesStart: '08:15', anesStop: '08:52', surgStart: '08:24', surgStop: '08:47' },
  },
  {
    id: 'AC-100494', batchId: 'b-hm-0612', patientFull: 'Elena Petrova', patient: 'Petrova, E.', mrn: 'MRN-4473550', dob: '02/17/1980', sex: 'F', ageYrs: 46,
    dos: 'Jun 12, 2026', facility: 'Houston Methodist', surgeon: 'R. Vance, MD', anesth: 'S. Ibrahim, MD',
    payer: 'Aetna', service: 'Outpatient', priority: 'med', status: 'unreviewed', receivedAgo: '5h',
    A: { surgical_cpt: '52356', anesthesia_cpt: '00918', primary_icd: 'N20.1', additional_icd: [], modifiers: ['QZ'], phys_status: 'P2' },
    B: { surgical_cpt: '52353', anesthesia_cpt: '00918', primary_icd: 'N20.1', additional_icd: ['N13.2'], modifiers: ['QZ'], phys_status: 'P2' },
    record: { indication: 'Obstructing distal ureteral calculus, right, with hydronephrosis', procedure: 'Cystoureteroscopy with laser lithotripsy and placement of indwelling ureteral stent', findings: '8 mm impacted distal ureteral stone fragmented with holmium laser; fragments extracted. A 6 Fr double-J stent was placed. Hydronephrosis noted proximally.', comorbidities: ['None significant'], airway: 'MAC', asaObserved: 'ASA II', technique: 'Monitored anesthesia care, CRNA non-medically-directed', agents: 'Propofol, fentanyl', anesStart: '12:05', anesStop: '12:52', surgStart: '12:15', surgStop: '12:46' },
  },
  // ===== BATCH b-bsw-0611 · Baylor Scott & White · Jun 11, 2026 (4 cases) =====
  {
    id: 'AC-100503', batchId: 'b-bsw-0611', patientFull: 'Margaret Foster', patient: 'Foster, M.', mrn: 'MRN-3390118', dob: '11/02/1951', sex: 'F', ageYrs: 74,
    dos: 'Jun 11, 2026', facility: 'Baylor Scott & White', surgeon: 'D. Reyes, MD', anesth: 'S. Ibrahim, MD',
    payer: 'Medicare', service: 'Outpatient', priority: 'med', status: 'unreviewed', receivedAgo: '1d',
    A: { surgical_cpt: '27447', anesthesia_cpt: '01402', primary_icd: 'M17.11', additional_icd: ['E66.9'], modifiers: ['QX'], phys_status: 'P2' },
    B: { surgical_cpt: '27447', anesthesia_cpt: '01402', primary_icd: 'M17.11', additional_icd: ['E66.9'], modifiers: ['QX'], phys_status: 'P3' },
    record: { indication: 'End-stage primary osteoarthritis of the right knee, refractory to conservative care', procedure: 'Right total knee arthroplasty', findings: 'Tricompartmental degenerative changes; components cemented in good position. Tourniquet time 74 min.', comorbidities: ['Morbid obesity, BMI 42', 'Obstructive sleep apnea on CPAP'], airway: 'Mallampati III; video laryngoscopy used', asaObserved: 'ASA III — morbid obesity with OSA', technique: 'Spinal anesthesia with sedation, CRNA under medical direction', agents: 'Bupivacaine (spinal), propofol infusion', anesStart: '10:05', anesStop: '11:40', surgStart: '10:28', surgStop: '11:32' },
  },
  {
    id: 'AC-100506', batchId: 'b-bsw-0611', patientFull: 'Patricia Adebayo', patient: 'Adebayo, P.', mrn: 'MRN-3345612', dob: '01/19/1974', sex: 'F', ageYrs: 52,
    dos: 'Jun 11, 2026', facility: 'Baylor Scott & White', surgeon: 'I. Garrison, MD', anesth: 'D. Chen, MD',
    payer: 'Aetna', service: 'Inpatient', priority: 'med', status: 'unreviewed', receivedAgo: '1d',
    A: { surgical_cpt: '58571', anesthesia_cpt: '00840', primary_icd: 'D25.9', additional_icd: ['N92.0'], modifiers: ['AA'], phys_status: 'P2' },
    B: { surgical_cpt: '58571', anesthesia_cpt: '00840', primary_icd: 'D25.9', additional_icd: ['N92.0', 'E66.01'], modifiers: ['AA'], phys_status: 'P3' },
    record: { indication: 'Symptomatic uterine leiomyomata with heavy menstrual bleeding', procedure: 'Total laparoscopic hysterectomy', findings: 'Enlarged fibroid uterus (~230 g); removed laparoscopically. EBL 150 mL.', comorbidities: ['Morbid obesity, BMI 41', 'Heavy menstrual bleeding, anemia'], airway: 'General endotracheal, Mallampati III', asaObserved: 'ASA III — morbid obesity', technique: 'General anesthesia, anesthesiologist personally performed', agents: 'Propofol, sevoflurane, rocuronium', anesStart: '12:40', anesStop: '14:25', surgStart: '13:02', surgStop: '14:10' },
  },
  {
    id: 'AC-100509', batchId: 'b-bsw-0611', patientFull: 'George Alvarez', patient: 'Alvarez, G.', mrn: 'MRN-3346904', dob: '08/03/1955', sex: 'M', ageYrs: 70,
    dos: 'Jun 11, 2026', facility: 'Baylor Scott & White', surgeon: 'P. Osei, MD', anesth: 'S. Ibrahim, MD',
    payer: 'Medicare', service: 'Outpatient', priority: 'med', status: 'unreviewed', receivedAgo: '1d',
    A: { surgical_cpt: '52204', anesthesia_cpt: '00910', primary_icd: 'R31.0', additional_icd: [], modifiers: ['AA'], phys_status: 'P2' },
    B: { surgical_cpt: '52204', anesthesia_cpt: '00910', primary_icd: 'C67.9', additional_icd: ['R31.0'], modifiers: ['AA'], phys_status: 'P2' },
    record: { indication: 'Painless gross hematuria; surveillance for bladder lesion', procedure: 'Cystourethroscopy with biopsy of bladder lesion', findings: '2 cm papillary lesion at the right lateral wall; cold-cup biopsies taken. Pathology pending at time of coding — no confirmed malignancy on the record.', comorbidities: ['Former smoker, 30 pack-years'], airway: 'MAC', asaObserved: 'ASA II', technique: 'Monitored anesthesia care, anesthesiologist personally performed', agents: 'Propofol', anesStart: '14:20', anesStop: '14:58', surgStart: '14:30', surgStop: '14:52' },
  },
  {
    id: 'AC-100512', batchId: 'b-bsw-0611', patientFull: 'Yuki Tanaka', patient: 'Tanaka, Y.', mrn: 'MRN-3348221', dob: '10/28/1968', sex: 'F', ageYrs: 57,
    dos: 'Jun 11, 2026', facility: 'Baylor Scott & White', surgeon: 'K. Duval, MD', anesth: 'D. Chen, MD',
    payer: 'Cigna', service: 'Outpatient', priority: 'low', status: 'unreviewed', receivedAgo: '1d',
    A: { surgical_cpt: '64721', anesthesia_cpt: '01810', primary_icd: 'G56.01', additional_icd: [], modifiers: ['QX'], phys_status: 'P1' },
    B: { surgical_cpt: '64721', anesthesia_cpt: '01810', primary_icd: 'G56.00', additional_icd: [], modifiers: ['QX'], phys_status: 'P1' },
    record: { indication: 'Right carpal tunnel syndrome, positive nerve conduction study, failed splinting', procedure: 'Open carpal tunnel release, right wrist', findings: 'Median nerve compression at the transverse carpal ligament, right; ligament released. Laterality clearly documented as right throughout the operative note.', comorbidities: ['Hypothyroidism, controlled'], airway: 'MAC with local by surgeon', asaObserved: 'ASA I', technique: 'MAC, CRNA under medical direction', agents: 'Propofol; lidocaine (local)', anesStart: '11:15', anesStop: '11:50', surgStart: '11:24', surgStop: '11:46' },
  },
  // ===== BATCH b-uts-0611 · UT Southwestern · Jun 11, 2026 (3 cases) =====
  {
    id: 'AC-100518', batchId: 'b-uts-0611', patientFull: 'James Carter', patient: 'Carter, J.', mrn: 'MRN-5582204', dob: '07/21/1966', sex: 'M', ageYrs: 59,
    dos: 'Jun 11, 2026', facility: 'UT Southwestern', surgeon: 'P. Raman, MD', anesth: 'M. Okonkwo, MD',
    payer: 'BCBS of Texas', service: 'Outpatient', priority: 'med', status: 'unreviewed', receivedAgo: '1d',
    A: { surgical_cpt: '45385', anesthesia_cpt: '00811', primary_icd: 'K63.5', additional_icd: ['Z12.11'], modifiers: ['QZ'], phys_status: 'P1' },
    B: { surgical_cpt: '45385', anesthesia_cpt: '00811', primary_icd: 'Z12.11', additional_icd: ['K63.5'], modifiers: ['QZ'], phys_status: 'P2' },
    record: { indication: 'Screening colonoscopy — average risk, converted to therapeutic on finding of polyp', procedure: 'Colonoscopy with snare polypectomy of a 9 mm sigmoid polyp', findings: 'Single 9 mm sessile polyp in sigmoid colon removed by snare; otherwise normal exam to cecum.', comorbidities: ['Well-controlled GERD'], airway: 'Not instrumented — MAC', asaObserved: 'ASA II — controlled reflux', technique: 'Monitored anesthesia care (MAC), CRNA non-medically-directed', agents: 'Propofol', anesStart: '13:10', anesStop: '13:46', surgStart: '13:18', surgStop: '13:40' },
  },
  {
    id: 'AC-100521', batchId: 'b-uts-0611', patientFull: 'William Hughes', patient: 'Hughes, W.', mrn: 'MRN-1120876', dob: '06/27/1962', sex: 'M', ageYrs: 63,
    dos: 'Jun 11, 2026', facility: 'UT Southwestern', surgeon: 'D. Reyes, MD', anesth: 'M. Okonkwo, MD',
    payer: 'Humana', service: 'Outpatient', priority: 'low', status: 'unreviewed', receivedAgo: '1d',
    A: { surgical_cpt: '43239', anesthesia_cpt: '00731', primary_icd: 'K21.0', additional_icd: [], modifiers: ['QZ'], phys_status: 'P2' },
    B: { surgical_cpt: '43239', anesthesia_cpt: '00731', primary_icd: 'K21.9', additional_icd: [], modifiers: ['QZ'], phys_status: 'P2' },
    record: { indication: 'Chronic reflux symptoms with dysphagia', procedure: 'EGD with biopsy', findings: 'Los Angeles Grade B reflux esophagitis with erosions; biopsies obtained. Hiatal hernia noted.', comorbidities: ['GERD', 'Former smoker'], airway: 'MAC', asaObserved: 'ASA II', technique: 'Monitored anesthesia care, CRNA non-medically-directed', agents: 'Propofol', anesStart: '14:00', anesStop: '14:34', surgStart: '14:08', surgStop: '14:28' },
  },
  {
    id: 'AC-100524', batchId: 'b-uts-0611', patientFull: 'Liam O’Brien', patient: 'O’Brien, L.', mrn: 'MRN-1121540', dob: '03/11/2019', sex: 'M', ageYrs: 7,
    dos: 'Jun 11, 2026', facility: 'UT Southwestern', surgeon: 'H. Castellano, MD', anesth: 'M. Okonkwo, MD',
    payer: 'Medicaid', service: 'Outpatient', priority: 'med', status: 'unreviewed', receivedAgo: '1d',
    A: { surgical_cpt: '42820', anesthesia_cpt: '00170', primary_icd: 'J35.3', additional_icd: ['G47.33'], modifiers: ['AA'], phys_status: 'P1' },
    B: { surgical_cpt: '42821', anesthesia_cpt: '00170', primary_icd: 'J35.3', additional_icd: [], modifiers: ['AA'], phys_status: 'P1' },
    record: { indication: 'Chronic tonsillar and adenoid hypertrophy with obstructive sleep-disordered breathing; patient age 7', procedure: 'Tonsillectomy and adenoidectomy', findings: '4+ obstructing tonsils and hypertrophic adenoids removed. Sleep study consistent with mild obstructive sleep apnea. Patient is 7 years old.', comorbidities: ['Obstructive sleep apnea (pediatric)'], airway: 'General endotracheal, oral RAE tube', asaObserved: 'ASA I', technique: 'General anesthesia, anesthesiologist personally performed', agents: 'Sevoflurane, propofol, dexamethasone', anesStart: '08:05', anesStop: '08:50', surgStart: '08:20', surgStop: '08:44' },
  },
  // ===== BATCH b-mh-0610 · Memorial Hermann · Jun 10, 2026 (2 cases) =====
  {
    id: 'AC-100530', batchId: 'b-mh-0610', patientFull: 'Ana Morales', patient: 'Morales, A.', mrn: 'MRN-6610489', dob: '04/09/1993', sex: 'F', ageYrs: 33,
    dos: 'Jun 10, 2026', facility: 'Memorial Hermann', surgeon: 'E. Vasquez, MD', anesth: 'R. Moura, MD',
    payer: 'Aetna', service: 'Inpatient', priority: 'high', status: 'unreviewed', receivedAgo: '2d',
    A: { surgical_cpt: '59514', anesthesia_cpt: '00840', primary_icd: 'O34.211', additional_icd: ['Z3A.39', 'Z37.0'], modifiers: ['AA'], phys_status: 'P2' },
    B: { surgical_cpt: '', anesthesia_cpt: '01961', primary_icd: 'O34.211', additional_icd: ['Z3A.39', 'Z37.0'], modifiers: ['AA', 'QS'], phys_status: 'P2' },
    record: { indication: 'Repeat cesarean delivery for prior low transverse scar, term gestation', procedure: 'Anesthesia for repeat cesarean section (low transverse)', findings: 'Live male infant delivered, Apgars 8/9. Estimated blood loss 700 mL.', comorbidities: ['Prior cesarean delivery'], airway: 'Neuraxial — no airway instrumentation', asaObserved: 'ASA II', technique: 'Spinal anesthesia for cesarean delivery — this is an anesthesia-primary claim, no surgical code billed by anesthesia', agents: 'Bupivacaine, fentanyl, morphine (intrathecal)', anesStart: '15:22', anesStop: '16:35', surgStart: '15:41', surgStop: '16:20' },
  },
  {
    id: 'AC-100533', batchId: 'b-mh-0610', patientFull: 'Grace Silva', patient: 'Silva, G.', mrn: 'MRN-4408890', dob: '08/14/2001', sex: 'F', ageYrs: 24,
    dos: 'Jun 10, 2026', facility: 'Memorial Hermann', surgeon: 'E. Vasquez, MD', anesth: 'R. Moura, MD',
    payer: 'BCBS of Texas', service: 'Inpatient', priority: 'high', status: 'submitted', receivedAgo: '2d',
    A: { surgical_cpt: '44970', anesthesia_cpt: '00840', primary_icd: 'K35.80', additional_icd: [], modifiers: ['AA'], phys_status: 'P2' },
    B: { surgical_cpt: '44970', anesthesia_cpt: '00840', primary_icd: 'K35.80', additional_icd: [], modifiers: ['AA'], phys_status: 'P2' },
    record: { indication: 'Acute appendicitis', procedure: 'Laparoscopic appendectomy', findings: 'Acutely inflamed, non-perforated appendix; removed laparoscopically.', comorbidities: ['None'], airway: 'General endotracheal, uneventful', asaObserved: 'ASA II (acute surgical condition)', technique: 'General anesthesia, anesthesiologist personally performed', agents: 'Propofol, sevoflurane, rocuronium', anesStart: '22:10', anesStop: '23:20', surgStart: '22:30', surgStop: '23:08' },
  },
  // ===== BATCH b-jps-0610 · JPS Health · Jun 10, 2026 (1 case, complete) =====
  {
    id: 'AC-100538', batchId: 'b-jps-0610', patientFull: 'Daniel Brooks', patient: 'Brooks, D.', mrn: 'MRN-7781120', dob: '02/18/1979', sex: 'M', ageYrs: 47,
    dos: 'Jun 10, 2026', facility: 'JPS Health', surgeon: 'L. Park, MD', anesth: 'CRNA I. Garrison · J. Bello, MD',
    payer: 'Cigna', service: 'Outpatient', priority: 'med', status: 'submitted', receivedAgo: '2d',
    A: { surgical_cpt: '49505', anesthesia_cpt: '00860', primary_icd: 'K40.90', additional_icd: [], modifiers: ['QX'], phys_status: 'P1' },
    B: { surgical_cpt: '49505', anesthesia_cpt: '00860', primary_icd: 'K40.90', additional_icd: [], modifiers: ['QY'], phys_status: 'P1' },
    record: { indication: 'Reducible right inguinal hernia', procedure: 'Open repair, initial right inguinal hernia with mesh', findings: 'Indirect inguinal hernia; mesh repair. No incarceration.', comorbidities: ['None significant'], airway: 'LMA general', asaObserved: 'ASA I', technique: 'General anesthesia. Anesthesiologist medically directing a single CRNA (1:1) for this case.', agents: 'Propofol, sevoflurane', anesStart: '11:02', anesStop: '11:58', surgStart: '11:20', surgStop: '11:50' },
  },
];

/** Batch-level metadata (client + 4-char job code from the FA registry). */
export const BATCH_META: Record<string, { client: string; jobCode: string }> = {
  'b-hm-0612': { client: 'Anesthesia Assoc of Greater Miami', jobCode: 'AMGA' },
  'b-bsw-0611': { client: 'United Anesthesia Services, PC', jobCode: 'UASA' },
  'b-uts-0611': { client: 'Delaware Anesthesia Assoc PA', jobCode: 'DELA' },
  'b-mh-0610': { client: 'Pediatric Anesthesia Assoc', jobCode: 'PAPA' },
  'b-jps-0610': { client: 'DuPage Valley Anesthesia Ltd', jobCode: 'DVAA' },
};

/** Human-coder assignment (cases split across coders by number range in a batch). */
export const CASE_CODER: Record<string, string> = {
  'AC-100482': 'cd1', 'AC-100485': 'cd1', 'AC-100488': 'cd1', 'AC-100491': 'cd2', 'AC-100494': 'cd2',
  'AC-100503': 'cd3', 'AC-100506': 'cd3', 'AC-100509': 'cd4', 'AC-100512': 'cd4',
  'AC-100518': 'cd1', 'AC-100521': 'cd1', 'AC-100524': 'cd2',
  'AC-100530': 'cd3', 'AC-100533': 'cd4', 'AC-100538': 'cd1',
};

/** Qualifier-flag values per case { a, b } — omitted = both off. */
const CASE_FLAGS: Record<string, { a?: Partial<Record<'emergency' | 'unusual_position', boolean>>; b?: Partial<Record<'emergency' | 'unusual_position', boolean>> }> = {
  'AC-100485': { a: { unusual_position: true }, b: { unusual_position: false } },
  'AC-100533': { a: { emergency: true }, b: { emergency: true } },
  'AC-100530': { a: { emergency: false }, b: { emergency: false } },
};

/** Deterministic per-case display flip (stable from the case id), matching the
 *  prototype's hash. Decides which display slot (A/B) each true source lands in. */
function flipFor(id: string): boolean {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h % 2 === 1;
}

/**
 * A demo FA episode id, shaped like a real one.
 *
 * Eight digits, no prefix — that is what EPISODE.EPISODE_ID actually looks like
 * (e.g. 70800563), and the shape MATTERS to the preview.
 *
 * These used to be `EP-` + the case id, which made them instantly
 * distinguishable from a DCN. Production offers no such help: an episode id and
 * a DCN are both bare digit strings, differing mainly in length, so the demo was
 * flattering exactly the design question the preview exists to answer — whether
 * a reviewer can tell the two identifiers apart on screen. It cannot be tested
 * against a fixture that makes them obviously different.
 *
 * Deterministic from the case id, so the preview, the tour and the tests all see
 * the same ids on every run.
 */
function demoEpisodeId(caseId: string): string {
  let h = 0;
  for (const ch of caseId) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  // Avalanche the hash before taking digits. The demo case ids are nearly
  // sequential and `h * 31 + c` is nearly linear over them, so without this
  // every id came out sharing a four-digit prefix — a column of 45351464,
  // 45351467, 45351470 reads as one number repeated, which is neither what FA
  // returns nor a fair test of whether the column is scannable.
  h ^= h >>> 16;
  h = Math.imul(h, 0x45d9f3b) >>> 0;
  // `>>> 0` after the LAST xor too, not only after the multiply. `^` yields a
  // SIGNED 32-bit int in JS, so without it h goes negative about half the time
  // and the id renders as "-60818729" — which is not an episode id, and looked
  // enough like one in a code review to survive.
  h = (h ^ (h >>> 16)) >>> 0;
  // 8 digits, never leading-zero — an id that renders 7 characters wide on some
  // rows and 8 on others would be a fixture artefact, not a real variation.
  return String(10_000_000 + (h % 90_000_000));
}

/**
 * The demo's facility id, shaped like FA's.
 *
 * FA's FACILITY_ID is a bare number (71796 in production). The demo used to put
 * the facility NAME in the id field, so the preview could never show what the
 * live screen shows: an unresolved facility rendering as a number a reviewer
 * cannot read. The same fake-vs-production drift the `EP-` episode ids had.
 */
function demoFacilityId(facility: string): string {
  let h = 0;
  for (const ch of facility) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x45d9f3b) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return String(10_000 + (h % 90_000));
}

/**
 * The demo's facility NAME — deliberately empty for ONE facility.
 *
 * FACILITY.FACILITY_NAME is nullable in FA, so an episode can carry a real id
 * and no name, and the view then falls back to the id with an "ID" tag. If every
 * demo case had a name that path would render nowhere — which is exactly how the
 * DCN fallback tag shipped unseen (PR #370, "known limitations").
 */
const DEMO_UNNAMED_FACILITY = 'JPS Health';

function demoFacilityName(facility: string): string {
  return facility === DEMO_UNNAMED_FACILITY ? '' : facility;
}

function withFlags(raw: RawCoded, flags: Partial<Record<'emergency' | 'unusual_position', boolean>> | undefined): CodedFields {
  return {
    ...raw,
    emergency: !!flags?.emergency,
    unusual_position: !!flags?.unusual_position,
  };
}

/**
 * INTERNAL cases — provenance-bearing. Only the data source consumes this (to
 * produce blind shapes) and, later, the aggregate dashboards. Never hand it to a
 * case/queue view.
 */
export const AUDIT_CASES_INTERNAL: AuditCaseInternal[] = RAW_CASES.map((c) => {
  const meta = BATCH_META[c.batchId] || { client: '', jobCode: '' };
  const flags = CASE_FLAGS[c.id] || {};
  const { A, B, ...rest } = c;
  return {
    // Preview data: renders the bundled synthetic record. Live cases carry a
    // real blob id, or empty when there is no stored document.
    documentBlobId: SAMPLE_DOCUMENT_BLOB_ID,
    ...rest,
    client: meta.client,
    jobCode: meta.jobCode,
    episodeId: demoEpisodeId(c.id),
    coderId: CASE_CODER[c.id] || 'cd1',
    coderName: '',
    flip: flipFor(c.id),
    agent: withFlags(A, flags.a),
    coder: withFlags(B, flags.b),
  };
});

/**
 * Convert an internal (provenance-bearing) case into the BLIND case a view sees:
 * apply the display flip to assign the A/B slots, then DROP `agent`/`coder`/`flip`
 * entirely. The returned object exposes results only as opaque `results.a` /
 * `results.b`. This is the choke point that enforces the blind-review invariant.
 */
export function toBlindCase(c: AuditCaseInternal): AuditCase {
  const { agent, coder, flip, ...core } = c;
  const [a, b] = flip ? [coder, agent] : [agent, coder];
  return {
    ...core,
    // The demo corpus stands in for a fully-populated, fully-permitted case.
    // Their ABSENCE means specific things on the live path — no coder leg, or a
    // grant that withholds PHI — and the review header now renders those
    // differently, so a preview without them would misrepresent both.
    episode: {
      facilityId: demoFacilityId(c.facility),
      facilityName: demoFacilityName(c.facility),
      serviceStartDate: c.dos,
      serviceEndDate: c.dos,
      placeOfService: '21',
      operatingRoom: '',
      anesthesiaType: c.anesth,
      providerIdOfRecord: c.surgeon,
      crnaIdOfRecord: '',
      coderId: c.coderId,
      coderName: '',
      codedWhen: c.dos,
      prStatus: 'C',
      cdStatus: 'C',
      peStatus: 'C',
    },
    patient_context: {
      lastName: c.patientFull,
      mrn: c.mrn,
      gender: c.sex,
      accountNumber: '',
      payer: c.payer,
      payerPlan: c.service,
    },
    // The demo corpus codes both sides on every case — that is what makes it a
    // useful preview of the comparison, and why the live path's one-sided
    // majority renders differently.
    hasA: true,
    hasB: true,
    results: { a, b },
  };
}

/** Blind case summary for the queue + batch case list (no result values beyond
 *  the flipped surgical-CPT teaser, which carries no slot provenance). */
export function toBatchCaseSummary(c: AuditCaseInternal): AuditBatchCaseSummary {
  // Apply the same per-case display flip the full blind case uses, so the
  // batch table's surgical-CPT A/B teaser lines up with the review screen and
  // never reveals which slot is the agent.
  const [a, b] = c.flip ? [c.coder, c.agent] : [c.agent, c.coder];
  return {
    id: c.id,
    episodeId: c.episodeId,
    patient: c.patient,
    patientFull: c.patientFull,
    mrn: c.mrn,
    payer: c.payer,
    procedure: c.record.procedure,
    status: c.status,
    coderId: c.coderId,
    // The demo corpus stands in for a fully-populated case, so both context
    // blocks are present. Their ABSENCE means specific things on the live path
    // — no coder leg yet, or a grant that does not confer PHI — and the preview
    // would misrepresent both by leaving them out.
    episode: {
      facilityId: demoFacilityId(c.facility),
      facilityName: demoFacilityName(c.facility),
      serviceStartDate: c.dos,
      serviceEndDate: c.dos,
      placeOfService: '21',
      operatingRoom: '',
      anesthesiaType: '',
      providerIdOfRecord: c.surgeon,
      crnaIdOfRecord: c.anesth,
      coderId: c.coderId,
      coderName: '',
      codedWhen: c.dos,
      prStatus: 'C',
      cdStatus: 'C',
      peStatus: 'C',
    },
    patient_context: {
      lastName: c.patientFull,
      mrn: c.mrn,
      gender: c.sex,
      accountNumber: '',
      payer: c.payer,
      payerPlan: '',
    },
    discrepancyCount: discrepancyCount(c.agent, c.coder),
    // The demo corpus carries BOTH sides on every case, which is what makes it
    // a useful preview — the live path is mostly one-sided today.
    comparable: true,
    comparedFieldCount: AUDIT_FIELDS.length,
    surgical: { a: a.surgical_cpt, b: b.surgical_cpt },
  };
}

function batchState(done: number, total: number): AuditBatchState {
  if (done === 0) return 'new';
  if (done === total) return 'complete';
  return 'active';
}

const MONTH_NUM: Record<string, string> = {
  jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
  jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12',
};

/**
 * Build the primary batch identifier `B-YYYY-MMDD-NN` from the batch's date of
 * service + a per-day sequence (NN, first-seen order within that day). A clean,
 * deterministic RCM-style number — the batch's lead identifier everywhere it is
 * shown. `dos` is the prototype's "Mon DD, YYYY" (e.g. "Jun 12, 2026").
 */
function batchNumberFor(dos: string, seq: number): string {
  const nn = String(seq).padStart(2, '0');
  const m = /^([A-Za-z]{3})\s+(\d{1,2}),\s+(\d{4})$/.exec(dos.trim());
  if (!m) return `B-${nn}`;
  const [, mon, day, year] = m;
  const mm = MONTH_NUM[mon.toLowerCase()] ?? '00';
  return `B-${year}-${mm}${day.padStart(2, '0')}-${nn}`;
}

/**
 * Group cases into batches, preserving
 * first-seen order — ported from the prototype's `buildBatches`. Emits only
 * aggregate counts + blind case summaries; no A/B result values, no provenance.
 */
export function buildBatches(cases: AuditCaseInternal[]): AuditBatch[] {
  const order: string[] = [];
  const groups = new Map<string, AuditCaseInternal[]>();
  for (const c of cases) {
    const key = c.batchId || `${c.dos}||${c.facility}`;
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(c);
  }
  // Per-day sequence counter (NN in the batch number), assigned in first-seen order.
  const perDaySeq = new Map<string, number>();
  return order.map((key) => {
    const list = groups.get(key)!;
    const first = list[0];
    const total = list.length;
    const done = list.filter((c) => c.status === 'submitted').length;
    const summaries = list.map(toBatchCaseSummary);
    const seq = (perDaySeq.get(first.dos) ?? 0) + 1;
    perDaySeq.set(first.dos, seq);
    const batchNumber = batchNumberFor(first.dos, seq);
    return {
      id: key,
      batchNumber,
      batchCode: batchNumber,
      dos: first.dos,
      // The demo dates are already ISO day strings, so Date.parse is
      // unambiguous here in a way it is not for the live source's
      // locale-formatted output. 0 for anything unparseable, which sorts last.
      dosSort: Date.parse(first.dos) || 0,
      client: first.client,
      jobCode: first.jobCode,
      caseCount: total,
      reviewedCount: done,
      remainingCount: total - done,
      discrepancyCaseCount: summaries.filter((s) => s.discrepancyCount > 0).length,
      comparableCaseCount: summaries.filter((s) => s.comparable).length,
      coders: [...new Set(list.map((c) => c.coderId))],
      // The demo roster resolves ids through AUDIT_CODERS, so the mock supplies
      // no server names — which also exercises the fallback path.
      coderNames: [],
      // Real per-coder case counts, positionally aligned with `coders` above, so
      // the preview exercises group-by-coder rather than collapsing to one
      // group. Built from the same list and in the same order.
      coderCaseCounts: [...new Set(list.map((c) => c.coderId))].map(
        (id) => list.filter((c) => c.coderId === id).length,
      ),
      state: batchState(done, total),
      // The demo corpus stands in for a fully-coded batch: every case carries
      // both sides, which is what "coding complete" means. Without this the
      // preview and the tour would open on an empty queue, since the list
      // defaults to reviewable batches.
      activeEpisodes: total,
      codedEpisodes: total,
      uncodedEpisodes: 0,
      inactiveEpisodes: 0,
      codingComplete: total > 0,
      declaredCount: total,
      codingStatus: 'C',
      faStatus: 'C',
      // The demo corpus has no FA lifecycle dates; empty is the honest value
      // and is what a batch with no FA record shows on the live path too.
      closeDate: '',
      qcDueDate: '',
      closeDateSort: 0,
      qcDueDateSort: 0,
      cases: summaries,
    };
  });
}
